import { slicedToArray as _slicedToArray, defineProperty as _defineProperty } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect } from 'react';
import { TextField, InternationalPhoneField, TextArea } from '@deal/components';
import { usePathContext } from '../../../context/PathContext.js';
import classNames from 'classnames';
import { InputTextPathQuestionSchemaRenderingStyle } from '../../../generated/types.js';
import omit from 'lodash/omit';
import styles from './styles.css.js';

var InputTextQuestion = function InputTextQuestion(_ref) {
  var question = _ref.question,
      loading = _ref.loading,
      onQuestionUpdated = _ref.onQuestionUpdated,
      onQuestionValidityChanged = _ref.onQuestionValidityChanged;

  var _a;

  var pathContext = usePathContext();
  var inputQuestion = question;

  var handleIsFieldValid = function handleIsFieldValid(value) {
    var isFieldValid = true;
    var errorMessage = ''; // Check the field level validations

    isFieldValid = inputQuestion.validations.every(function (validation) {
      if (validation.__typename === 'InputTextPathQuestionSchemaLengthValidationViewModel') {
        if (validation.minimumLength && value.length < validation.minimumLength) {
          return false;
        }

        if (validation.maximumLength && value.length > validation.maximumLength) {
          return false;
        }
      } else if (validation.__typename === 'InputTextPathQuestionSchemaRegexValidationViewModel') {
        if (!new RegExp(validation.regex).test(value)) {
          errorMessage = validation.errorMessage;
          return false;
        }
      }

      return true;
    }); // Check to see if this is a required question

    if (question.commonFields.required && !value) {
      isFieldValid = false;
      errorMessage = 'This question is required';
    }

    return {
      isFieldValid: isFieldValid,
      errorMessage: errorMessage
    };
  };

  var initialInputValue = inputQuestion.inputTextValue ? inputQuestion.inputTextValue : '';

  var _useState = useState(initialInputValue),
      _useState2 = _slicedToArray(_useState, 2),
      inputValue = _useState2[0],
      setInputValue = _useState2[1];

  var isInitialFieldValid = initialInputValue ? handleIsFieldValid(initialInputValue).isFieldValid : undefined;

  var _useState3 = useState(isInitialFieldValid),
      _useState4 = _slicedToArray(_useState3, 2),
      isValid = _useState4[0],
      setIsValid = _useState4[1];

  var _useState5 = useState(undefined),
      _useState6 = _slicedToArray(_useState5, 2),
      errorText = _useState6[0],
      setErrorText = _useState6[1];

  useEffect(function () {
    if (initialInputValue) {
      populateInputValue(initialInputValue);
    }
  }, []);

  var populateInputValue = function populateInputValue(updatedInputValue) {
    var _a;

    setInputValue(updatedInputValue);
    var isFieldValidResponse = handleIsFieldValid(updatedInputValue);

    if (isFieldValidResponse.isFieldValid) {
      setIsValid(true);
      setErrorText(undefined);
    } else {
      setIsValid(false);
      setErrorText(isFieldValidResponse.errorMessage || 'Please enter a valid value');
    }

    onQuestionUpdated({
      id: (_a = pathContext === null || pathContext === void 0 ? void 0 : pathContext.activeNode) === null || _a === void 0 ? void 0 : _a.id,
      inputTextQuestion: {
        questionId: question.id,
        value: updatedInputValue || null
      }
    });
    onQuestionValidityChanged(isFieldValidResponse.isFieldValid);
  };

  var inputAttributes = {
    value: inputValue,
    disabled: loading,
    placeholder: (_a = inputQuestion.placeholder) === null || _a === void 0 ? void 0 : _a.plainText,
    name: question.id,
    error: !isValid && isValid !== undefined,
    errorText: errorText,
    label: '',
    labelHidden: true
  };
  var inputClasses = classNames(styles.input, _defineProperty({}, styles.placementModal, pathContext.pathPlacement === 'modal'));

  switch (inputQuestion.inputTextRenderingStyle) {
    case InputTextPathQuestionSchemaRenderingStyle.TEXT_AREA:
      return React.createElement("div", {
        className: inputClasses,
        "data-testid": "path-text-area-container"
      }, React.createElement(TextArea, Object.assign({
        rows: 6,
        onChange: function onChange(event) {
          return populateInputValue(event.target.value);
        }
      }, omit(inputAttributes, 'errorText'))));

    case InputTextPathQuestionSchemaRenderingStyle.NUMERIC:
      return React.createElement("div", {
        className: inputClasses,
        "data-testid": "path-numeric-text-field-container"
      }, React.createElement(TextField, Object.assign({
        autoFocus: false,
        type: "tel",
        onChange: function onChange(event) {
          return populateInputValue(event.target.value);
        }
      }, inputAttributes)));

    case InputTextPathQuestionSchemaRenderingStyle.TELEPHONE:
      /**
       * Use the `inputTextParsedPhoneNumberValue` here since this component supports
       * a region and national code. This behaves the same was a `defaultValue` in that
       * it continues to present the component as uncontrolled.
       */
      return React.createElement("div", {
        className: inputClasses,
        "data-testid": "path-phone-field-container"
      }, React.createElement(InternationalPhoneField, Object.assign({
        autoFocus: false,
        parsedPhoneNumber: inputQuestion.inputTextParsedPhoneNumberValue,
        onChange: function onChange(phoneValue) {
          return populateInputValue(phoneValue);
        }
      }, omit(inputAttributes, 'value'))));

    case InputTextPathQuestionSchemaRenderingStyle.EMAIL:
      return React.createElement("div", {
        className: inputClasses,
        "data-testid": "path-email-field-container"
      }, React.createElement(TextField, Object.assign({
        autoFocus: false,
        type: "email",
        testId: "email-input",
        onChange: function onChange(event) {
          return populateInputValue(event.target.value);
        }
      }, inputAttributes)));

    case InputTextPathQuestionSchemaRenderingStyle.INPUT_FIELD:
      return React.createElement("div", {
        className: inputClasses,
        "data-testid": "path-text-field-container"
      }, React.createElement(TextField, Object.assign({
        autoFocus: false,
        onChange: function onChange(event) {
          return populateInputValue(event.target.value);
        }
      }, inputAttributes)));
  }
};

export default InputTextQuestion;
//# sourceMappingURL=index.js.map
